import { useTranslation } from "react-i18next";

import type {
  ExportHistoryType,
  InvoiceData,
  InvoiceDetails,
} from "@/apis/gql/types";
import type { DatevError } from "@/components/shared/errors/datev-error-message";
import { DatevErrorMessage } from "@/components/shared/errors/datev-error-message";
import { ServerErrorMessage } from "@/components/shared/errors/server-error-message";
import { ExportsValidationErrors } from "@/components/shared/exports/validation-errors";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { AlertOctagon } from "lucide-react";

type Props = {
  invoice: {
    errorReason?: InvoiceData["errorReason"];
    exports: Pick<ExportHistoryType, "exportedAt" | "results">[];
    validationErrors?: InvoiceData["validationErrors"];
  };
};

export const InvoiceExportAlert = ({ invoice }: Props) => {
  const { t } = useTranslation();

  const errors = invoice.errorReason;
  const validationErrors = invoice.validationErrors;
  const exports = invoice.exports ?? [];

  const [datevExport] = [...exports].sort(
    // @ts-ignore
    (a: string, b: string) => new Date(b.exportedAt) - new Date(a.exportedAt),
  );
  const results = datevExport?.results?.errors ?? {};

  const datevErrors = results.validation_details;

  const renderErrors = () => {
    if (validationErrors) {
      return <ExportsValidationErrors errors={validationErrors} />;
    }

    if (errors) {
      return (
        <ServerErrorMessage
          code={errors.code || (errors.data as any)?.error}
          data={errors.data}
        />
      );
    }

    if (datevErrors) {
      return <DatevErrorMessage datevError={datevErrors} />;
    }

    return (
      <ServerErrorMessage
        code={results.code || (results.data as any)?.error}
        data={results.data}
        uri={(results.data as any)?.error_uri}
      />
    );
  };

  return (
    <Alert variant="destructive">
      <AlertTitle className="font-bold flex items-center">
        <AlertOctagon size={20} className="mr-2" />
        {t("invoice_details.export_alert.title")}
      </AlertTitle>
      <AlertDescription>{renderErrors()}</AlertDescription>
    </Alert>
  );
};
